import { useState, useCallback } from 'react';
import CrosswordBoard    from './CrosswordBoard';
import ChainReactionBoard from './ChainReactionBoard';
import AdminPanel        from './AdminPanel';
import AmericaSaysBoard  from './AmericaSaysBoard';
import GeoGuessBoard     from './GeoGuessBoard';
import DropPanBoard      from './DropPanBoard';
import BracketBoard      from './BracketBoard';
import TVDisplay         from './TVDisplay';
import Scoreboard        from './Scoreboard';

// ─── Game ID Entry Screen ─────────────────────────────────────────────────────
function GameEntry({ onLaunch }) {
  const [gameId,   setGameId]   = useState('');
  const [gameType, setGameType] = useState('crossword');
  const [error,    setError]    = useState('');

  const launch = () => {
    const id = gameId.trim().toUpperCase();
    if (!id) { setError('Please enter a Game ID'); return; }
    const u = new URL(window.location.href);
    u.searchParams.set('game', id);
    u.searchParams.set('type', gameType);
    u.searchParams.set('view', 'display');
    window.history.replaceState(null, '', u.toString());
    onLaunch({ gameId: id, gameType });
  };

  return (
    <div style={{
      height: '100vh',
      background: 'radial-gradient(ellipse at 60% 40%, #1a0a3d 0%, #0a0818 60%, #020208 100%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Barlow', sans-serif", color: '#fff',
      overflow: 'hidden', position: 'relative',
    }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes orbFloat1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(40px,-30px) scale(1.1)} }
        @keyframes orbFloat2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-35px,28px) scale(0.9)} }
        @keyframes orbFloat3 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(20px,40px) scale(1.07)} }
        @keyframes fadeInUp  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulseGlow { 0%,100%{box-shadow:0 0 30px rgba(255,140,0,.4)} 50%{box-shadow:0 0 70px rgba(255,140,0,.8),0 0 130px rgba(255,100,0,.3)} }
        .game-id-input {
          background: rgba(255,255,255,.06);
          border: 2px solid rgba(255,179,0,.3);
          border-radius: 14px; color: #fff;
          font-size: 36px; font-weight: 900;
          font-family: 'Bebas Neue', sans-serif;
          letter-spacing: 10px; text-align: center;
          padding: 16px 24px; width: 320px; outline: none;
          transition: border-color .25s, box-shadow .25s;
          text-transform: uppercase;
        }
        .game-id-input:focus {
          border-color: rgba(255,179,0,.8);
          box-shadow: 0 0 0 4px rgba(255,179,0,.1), 0 0 30px rgba(255,140,0,.2);
        }
        .game-id-input::placeholder { color: rgba(255,179,0,.2); letter-spacing: 6px; }
        .launch-btn {
          background: linear-gradient(135deg, #FF8C00, #FFB300);
          border: none; border-radius: 14px;
          color: #1a0a00; font-size: 18px; font-weight: 900;
          font-family: 'Bebas Neue', sans-serif; letter-spacing: 4px;
          padding: 16px 48px; cursor: pointer; width: 320px;
          box-shadow: 0 0 30px rgba(255,140,0,.4);
          transition: transform .15s, box-shadow .15s;
        }
        .launch-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 40px rgba(255,140,0,.6); }
        .launch-btn:active { transform: translateY(0); }
        .type-tab {
          padding: 8px 22px; border-radius: 8px; cursor: pointer;
          font-size: 11px; font-weight: 700; font-family: 'Barlow Condensed', sans-serif;
          letter-spacing: 3px; border: 1px solid transparent; transition: all .2s;
          background: transparent; color: rgba(255,255,255,.3);
        }
        .type-tab.active {
          background: rgba(255,179,0,.14); color: #FFB300;
          border-color: rgba(255,179,0,.4); box-shadow: 0 0 12px rgba(255,179,0,.2);
        }
      `}</style>

      <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' }}>
        <div style={{ position:'absolute', width:'65vw', height:'65vw', top:'-25%', left:'-20%',
          background:'radial-gradient(circle, rgba(255,140,0,.08) 0%, transparent 65%)',
          animation:'orbFloat1 9s ease infinite' }} />
        <div style={{ position:'absolute', width:'55vw', height:'55vw', bottom:'-20%', right:'-15%',
          background:'radial-gradient(circle, rgba(120,60,220,.08) 0%, transparent 65%)',
          animation:'orbFloat2 12s ease infinite 2s' }} />
        <div style={{ position:'absolute', width:'40vw', height:'40vw', top:'40%', left:'40%',
          background:'radial-gradient(circle, rgba(0,180,220,.06) 0%, transparent 65%)',
          animation:'orbFloat3 15s ease infinite 5s' }} />
        <div style={{
          position:'absolute', inset:0,
          backgroundImage:`linear-gradient(rgba(255,255,255,.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.02) 1px,transparent 1px)`,
          backgroundSize:'60px 60px',
        }} />
      </div>

      <div style={{
        position:'relative', zIndex:10,
        display:'flex', flexDirection:'column', alignItems:'center', gap:28,
        animation:'fadeInUp .8s cubic-bezier(.34,1.4,.64,1) both',
      }}>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
          <div style={{
            width:80, height:80, borderRadius:22,
            background:'linear-gradient(135deg, #FF8C00, #FFB300)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:44, boxShadow:'0 0 40px rgba(255,140,0,.55)',
            animation:'pulseGlow 3s ease infinite',
          }}>🎯</div>
          <div style={{ fontSize:48, fontWeight:700, fontFamily:"'Bebas Neue',sans-serif",
            letterSpacing:6, color:'#fff', textShadow:'0 0 30px rgba(255,179,0,.5)' }}>
            Game Display
          </div>
          <div style={{ fontSize:11, letterSpacing:6, color:'rgba(255,179,0,.5)',
            fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
            LIVE BOARD LAUNCHER
          </div>
        </div>

        <div style={{ width:240, height:1, background:'linear-gradient(90deg,transparent,rgba(255,179,0,.3),transparent)' }} />

        <div style={{ display:'flex', gap:6,
          background:'rgba(0,0,0,.3)', borderRadius:10, padding:4,
          border:'1px solid rgba(255,255,255,.06)' }}>
          {[['americasays','America Says'],['crossword','Crossword'],['geoguess','GeoGuess'],['droppan','Drop Pan'],['chainreaction','Chain Reaction'],['bracket','Bracket']].map(([val,label]) => (
            <button key={val} className={`type-tab${gameType===val?' active':''}`}
              onClick={() => setGameType(val)}>{label}</button>
          ))}
        </div>

        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
          <label style={{ fontSize:10, letterSpacing:5, color:'rgba(255,179,0,.5)',
            fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
            ENTER GAME ID
          </label>
          <input
            className="game-id-input"
            type="text"
            placeholder="ABC123"
            value={gameId}
            maxLength={12}
            onChange={e => { setGameId(e.target.value.toUpperCase()); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && launch()}
            autoFocus
          />
          {error && (
            <div style={{ fontSize:12, color:'#FF4B4B', fontFamily:"'Barlow Condensed',sans-serif",
              letterSpacing:2, fontWeight:700 }}>{error}</div>
          )}
        </div>

        <button className="launch-btn" onClick={launch}>▶ LAUNCH BOARD</button>

        <div style={{ fontSize:11, color:'rgba(255,255,255,.15)',
          fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textAlign:'center', maxWidth:300 }}>
          The game ID is provided by your game host.<br/>
          This screen loads the live display board.
        </div>
      </div>
    </div>
  );
}

// ─── SPA Router ───────────────────────────────────────────────────────────────
const typeToScreen = (t) => ['americasays','geoguess','droppan','chainreaction','bracket'].includes(t) ? t : 'crossword';

// Manages routing as React state so boards can navigate back to GameEntry
// without a page reload (preserving the audio context and user-gesture unlock).
export default function App() {
  const path = window.location.pathname;

  // Initialise from URL so direct links / refreshes still work
  const initParams = new URLSearchParams(window.location.search);
  const [route, setRoute] = useState(() => {
    const gid  = initParams.get('game');
    const type = initParams.get('type') || 'crossword';
    if (gid) return {
      screen: typeToScreen(type),
      gameId: gid,
    };
    return { screen: 'entry' };
  });

  const goEntry = useCallback(() => {
    // Clear the game params from the URL bar so a refresh lands on GameEntry
    const u = new URL(window.location.href);
    u.searchParams.delete('game');
    u.searchParams.delete('type');
    u.searchParams.delete('view');
    window.history.replaceState(null, '', u.toString());
    setRoute({ screen: 'entry' });
  }, []);

  const handleLaunch = useCallback(({ gameId, gameType }) => {
    setRoute({ screen: typeToScreen(gameType), gameId });
  }, []);

  const handleAutoLaunch = useCallback(({ gameId, gameType }) => {
    // Update URL so a refresh reconnects to the new game
    const u = new URL(window.location.href);
    u.searchParams.set('game', gameId);
    u.searchParams.set('type', gameType);
    u.searchParams.set('view', 'display');
    window.history.replaceState(null, '', u.toString());
    setRoute({ screen: typeToScreen(gameType), gameId });
  }, []);

  if (path.startsWith('/admin'))      return <AdminPanel />;
  if (path.startsWith('/tv'))         return <TVDisplay />;
  if (path.startsWith('/scoreboard')) return <Scoreboard />;

  if (route.screen === 'americasays') {
    return <AmericaSaysBoard key={route.gameId} gameId={route.gameId} onExit={goEntry} onAutoLaunch={handleAutoLaunch} />;
  }
  if (route.screen === 'geoguess') {
    return <GeoGuessBoard key={route.gameId} gameId={route.gameId} onExit={goEntry} />;
  }
  if (route.screen === 'droppan') {
    return <DropPanBoard key={route.gameId} gameId={route.gameId} onExit={goEntry} />;
  }
  if (route.screen === 'bracket') {
    return <BracketBoard key={route.gameId} gameId={route.gameId} onExit={goEntry} />;
  }
  if (route.screen === 'chainreaction') {
    return <ChainReactionBoard key={route.gameId} gameId={route.gameId} />;
  }
  if (route.screen === 'crossword') {
    return <CrosswordBoard key={route.gameId} />;
  }
  return <GameEntry onLaunch={handleLaunch} />;
}
